import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authActions } from '../redux/auth/actions';
import { normalizeUserProfile } from '../utils/commerce';

const TOKEN_KEY = 'sedyam_token';
const PROFILE_KEY = 'sedyam_profile';

export const useAuthStore = (selector) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);

  const setUserProfile = useCallback(
    (profile) => dispatch(authActions.setUserProfile(profile)),
    [dispatch]
  );

  const setLoading = useCallback(
    (loading) => dispatch(authActions.setLoading(loading)),
    [dispatch]
  );

  const setToken = useCallback((token) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  }, []);

  const setBackendProfile = useCallback(
    (profile) => {
      const normalizedProfile = normalizeUserProfile(profile);
      localStorage.setItem(PROFILE_KEY, JSON.stringify(normalizedProfile));
      dispatch(authActions.setUserProfile(normalizedProfile));
    },
    [dispatch]
  );

  const logout = useCallback(() => {
    dispatch(authActions.logout());
  }, [dispatch]);

  const dispatchAsync = useCallback(
    (requestAction, payload) =>
      new Promise((resolve, reject) => {
        dispatch(requestAction(payload, { resolve, reject }));
      }),
    [dispatch]
  );

  const bootstrapSession = useCallback(
    () => dispatchAsync(authActions.bootstrapSessionRequest),
    [dispatchAsync]
  );

  const login = useCallback(
    (payload) => dispatchAsync(authActions.loginRequest, payload),
    [dispatchAsync]
  );

  const register = useCallback(
    (payload) => dispatchAsync(authActions.registerRequest, payload),
    [dispatchAsync]
  );

  const fetchMe = useCallback(
    () => dispatchAsync(authActions.fetchMeRequest),
    [dispatchAsync]
  );

  const updateMe = useCallback(
    (payload) => dispatchAsync(authActions.updateMeRequest, payload),
    [dispatchAsync]
  );

  const changePassword = useCallback(
    (payload) => dispatchAsync(authActions.changePasswordRequest, payload),
    [dispatchAsync]
  );

  const setupSuperAdmin = useCallback(
    (payload) => dispatchAsync(authActions.setupSuperAdminRequest, payload),
    [dispatchAsync]
  );

  const storeShape = useMemo(
    () => ({
      userProfile: auth.userProfile,
      loading: auth.loading,
      pending: auth.pending,
      error: auth.error,
      setUserProfile,
      setLoading,
      setToken,
      setBackendProfile,
      logout,
      bootstrapSession,
      login,
      register,
      fetchMe,
      updateMe,
      changePassword,
      setupSuperAdmin,
    }),
    [
      auth.error,
      auth.loading,
      auth.pending,
      auth.userProfile,
      bootstrapSession,
      changePassword,
      fetchMe,
      login,
      logout,
      register,
      setBackendProfile,
      setLoading,
      setupSuperAdmin,
      setToken,
      setUserProfile,
      updateMe,
    ]
  );

  return typeof selector === 'function' ? selector(storeShape) : storeShape;
};
